import { useRef, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight,
  User,
  KeyRound,
  SlidersHorizontal,
  Terminal,
  type LucideIcon,
} from "lucide-react";
import {
  updateProfileInputSchema,
  changePasswordInputSchema,
  type UpdateProfileInput,
  type ChangePasswordInput,
} from "@repo/shared";
import {
  Alert,
  Button,
  Field,
  Form,
  FormActions,
  Input,
  Mono,
  NavigationMenu,
  PageContent,
  PageHeader,
  PageShell,
  PropertyList,
  Segmented,
  SettingsLayout,
  SettingsSection,
  UserChip,
  type NavigationItem,
} from "@h2j-data-team/ui";
import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import { usePasswordChangedOnH2j, useSsoStatus } from "@/features/sso/api";
import { authClient } from "@/lib/auth-client";
import { APP_VERSION } from "@/lib/version";
import { ApiKeysSection } from "@/features/api-keys/components/api-keys-section";

export const Route = createFileRoute("/_authenticated/profile")({
  staticData: { pageTitle: "breadcrumbs.profile" },
  component: ProfilePage,
});

type ProfileSection = "personal" | "password" | "preferences" | "apiKeys";

const SECTIONS: { id: ProfileSection; icon: LucideIcon }[] = [
  { id: "personal", icon: User },
  { id: "password", icon: KeyRound },
  { id: "preferences", icon: SlidersHorizontal },
  { id: "apiKeys", icon: Terminal },
];

function ProfilePage() {
  const { user } = Route.useRouteContext();
  const { confirm, dialogProps } = useConfirmDialog();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("profile");
  const [section, setSection] = useState<ProfileSection>("personal");
  // Without a local password, or after the switch for a linked account, the
  // password is changed on Login H2J.
  const { enabled: ssoEnabled, ssoOnly, passwordUrl } = useSsoStatus();
  const passwordManagedByH2j = usePasswordChangedOnH2j(ssoEnabled, ssoOnly);
  const deleteButton = useRef<HTMLButtonElement>(null);

  const profileForm = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileInputSchema),
    defaultValues: {
      name: user?.name ?? "",
    },
  });

  const passwordForm = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordInputSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onProfileSubmit = async (data: UpdateProfileInput) => {
    try {
      await authClient.updateUser({ name: data.name });
      toast.success(t("toast.profileUpdated"));
    } catch (error) {
      toast.error(
        t("toast.profileFailed", {
          error: error instanceof Error ? error.message : "Unknown error",
        }),
      );
    }
  };

  const onPasswordSubmit = async (data: ChangePasswordInput) => {
    try {
      await authClient.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      passwordForm.reset();
      toast.success(t("toast.passwordChanged"));
    } catch (error) {
      toast.error(
        t("toast.passwordFailed", {
          error: error instanceof Error ? error.message : "Unknown error",
        }),
      );
    }
  };

  const handleDeleteAccount = () => {
    confirm({
      title: t("deleteAccount.title"),
      description: t("deleteAccount.description"),
      confirmLabel: t("deleteAccount.confirmLabel"),
      variant: "destructive",
      restoreFocusRef: deleteButton,
      onConfirm: async () => {
        try {
          await authClient.deleteUser();
          navigate({
            to: "/login",
            search: {
              mode: undefined,
              token: undefined,
              error: undefined,
              redirect: undefined,
              email: undefined,
            },
          });
        } catch {
          toast.error(t("toast.deleteFailed"));
        }
      },
    });
  };

  const items: NavigationItem[] = SECTIONS.map(({ id, icon: Icon }) => ({
    id,
    icon: <Icon />,
    active: section === id,
    onSelect: () => setSection(id),
    label: t(`nav.${id}.label`),
    description: t(`nav.${id}.description`),
  }));

  return (
    <>
      <PageShell header={<PageHeader title={t("pageTitle")} description={t("description")} />}>
        <PageContent>
          <SettingsLayout navigation={<NavigationMenu label={t("nav.ariaLabel")} items={items} />}>
            {section === "personal" && (
              <>
                <SettingsSection
                  title={t("personalInfo.title")}
                  description={t("personalInfo.description")}
                  footer={
                    <FormActions
                      busy={profileForm.formState.isSubmitting}
                      submitLabel={t("saveChanges")}
                      submittingLabel={t("saving")}
                      submitProps={{ form: "profile-form" }}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => profileForm.reset({ name: user?.name ?? "" })}
                      >
                        {t("cancel")}
                      </Button>
                    </FormActions>
                  }
                >
                  <Form
                    id="profile-form"
                    busy={profileForm.formState.isSubmitting}
                    onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                  >
                    <UserChip
                      name={user?.name ?? user?.email ?? ""}
                      detail={user?.email}
                      size="lg"
                    />
                    <Field
                      label={t("name.label")}
                      error={profileForm.formState.errors.name?.message}
                      className="max-w-[420px]"
                    >
                      <Input
                        placeholder={t("name.placeholder")}
                        autoComplete="name"
                        {...profileForm.register("name")}
                      />
                    </Field>
                  </Form>
                </SettingsSection>

                <Alert
                  tone="danger"
                  role="note"
                  title={t("dangerZone.deleteAccount")}
                  action={
                    <Button ref={deleteButton} variant="danger" onClick={handleDeleteAccount}>
                      {t("dangerZone.deleteButton")}
                    </Button>
                  }
                >
                  {t("dangerZone.warning")}
                </Alert>
              </>
            )}

            {section === "password" && passwordManagedByH2j && (
              <Alert
                tone="info"
                role="note"
                title={t("password.h2j.title")}
                action={
                  passwordUrl ? (
                    <Button asChild variant="secondary">
                      <a href={passwordUrl} target="_blank" rel="noopener noreferrer">
                        {t("password.h2j.action")}
                        <ArrowUpRight aria-hidden />
                      </a>
                    </Button>
                  ) : undefined
                }
              >
                {t("password.h2j.description")}
              </Alert>
            )}

            {section === "password" && !passwordManagedByH2j && (
              <SettingsSection
                title={t("password.title")}
                footer={
                  <FormActions
                    busy={passwordForm.formState.isSubmitting}
                    submitLabel={t("password.change")}
                    submittingLabel={t("password.changing")}
                    submitProps={{ form: "password-form" }}
                  />
                }
              >
                <Form
                  id="password-form"
                  busy={passwordForm.formState.isSubmitting}
                  onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                >
                  {/* Hidden username field for password managers / a11y */}
                  <input
                    type="text"
                    value={user?.email ?? ""}
                    autoComplete="username"
                    className="hidden"
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <Field
                    label={t("password.current")}
                    error={passwordForm.formState.errors.currentPassword?.message}
                    className="max-w-[420px]"
                  >
                    <Input
                      type="password"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      {...passwordForm.register("currentPassword")}
                    />
                  </Field>
                  <Field
                    label={t("password.new")}
                    error={passwordForm.formState.errors.newPassword?.message}
                    className="max-w-[420px]"
                  >
                    <Input
                      type="password"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      {...passwordForm.register("newPassword")}
                    />
                  </Field>
                  <Field
                    label={t("password.confirm")}
                    error={passwordForm.formState.errors.confirmPassword?.message}
                    className="max-w-[420px]"
                  >
                    <Input
                      type="password"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      {...passwordForm.register("confirmPassword")}
                    />
                  </Field>
                </Form>
              </SettingsSection>
            )}

            {section === "preferences" && (
              <>
                <SettingsSection title={t("preferences")}>
                  <Field group label={t("language.title")} hint={t("language.description")}>
                    {(control) => (
                      <Segmented
                        aria-labelledby={control["aria-labelledby"]}
                        aria-describedby={control["aria-describedby"]}
                        value={i18n.language === "pt-BR" ? "pt-BR" : "en"}
                        onValueChange={(code) => void i18n.changeLanguage(code)}
                        options={[
                          { value: "en", label: t("language.en") },
                          { value: "pt-BR", label: t("language.ptBR") },
                        ]}
                      />
                    )}
                  </Field>
                </SettingsSection>
                <SettingsSection title={t("about.title")} description={t("about.description")}>
                  <PropertyList
                    layout="inline"
                    items={[
                      {
                        id: "version",
                        label: t("about.appVersion"),
                        value: (
                          <span className="flex items-center gap-3">
                            <Mono>v{APP_VERSION}</Mono>
                            <Button asChild variant="link" size="compact">
                              <Link to="/changelog">{t("about.viewReleaseNotes")}</Link>
                            </Button>
                          </span>
                        ),
                      },
                    ]}
                  />
                </SettingsSection>
              </>
            )}

            {section === "apiKeys" && <ApiKeysSection />}
          </SettingsLayout>
        </PageContent>
      </PageShell>

      <ConfirmDialog {...dialogProps} />
    </>
  );
}
